import React, { useCallback, useState, useMemo } from 'react';
import InputNumberFormat from 'react-number-format';

import { api } from 'services/api';
import { shouldTreatSuggestedAsCompositionOriginal } from 'shared/budget-component-type';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Flex,
  Grid,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Heading,
  Input as ChakraInput,
  Text,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

import {
  Composition,
  Input,
  CompositionCustom,
} from 'pages/Budget/Add/SubPages/Planning/types';

import useAlert from 'hooks/useAlert';

type BudgetComponent = Composition | Input | CompositionCustom;

type BDIPayload = {
  [componentId: number]: number | null;
};

type FormErrors = {
  [key: string]: string | boolean;
};

interface ModalBudgetComponentBDIProps extends ModalRootProps {
  data: {
    budgetId: number;
    component: BudgetComponent;
  };
}

const ModalBudgetComponentBDI: React.FC<ModalBudgetComponentBDIProps> = ({
  onConfirm,
  handleClose,
  data: { budgetId, component },
  ...restProps
}) => {
  const { openCustomAlert } = useAlert();

  const [bdiPercent, setBDIPercent] = useState(component.bdi_percent);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<FormErrors | undefined>();

  const isComposition = (c: BudgetComponent): c is Composition => {
    return shouldTreatSuggestedAsCompositionOriginal({
      type: c.type,
      total: c.total,
    });
  };

  const isInput = (c: BudgetComponent): c is Input => {
    return c.type === 'input_original';
  };

  const isCustomComposition = (r: BudgetComponent): boolean => {
    return r.type === 'composition_custom';
  };

  const description = useMemo(() => {
    if (isComposition(component)) {
      return (
        component.composition_price?.custom_description ||
        component.composition_price?.composition.description
      );
    }

    if (isInput(component)) {
      return (
        component.input_price?.custom_description ||
        component.input_price?.input.description
      );
    }

    if (isCustomComposition(component)) {
      return component.budget_composition_price.description;
    }

    return '';
  }, [component]);

  const checkUsage = useCallback(
    async (c: BudgetComponent): Promise<BudgetComponent[]> => {
      if (!c) {
        return [];
      }

      if (isCustomComposition(c)) {
        return [c];
      }

      const { data: response } = await api.get(
        `/budget/${budgetId}/component`,
        {
          params: {
            ...(isComposition(c)
              ? { 'filter[composition_price_id]': c.composition_price.id }
              : {}),

            ...(isInput(c)
              ? { 'filter[input_price_id]': c.input_price.id }
              : {}),
          },
        },
      );

      return response.data;
    },
    [budgetId],
  );

  const handleSubmitCustomBDI = useCallback(
    async (ids: number[]) => {
      const payload = ids.reduce((acc, id) => {
        const bdi = (() => {
          if (bdiPercent !== null && bdiPercent !== undefined) {
            return bdiPercent;
          }

          return null;
        })();

        acc[id] = bdi;
        return acc;
      }, {} as BDIPayload);

      try {
        await api.put(`/budget/${budgetId}/component/bdi`, {
          budget_components: payload,
        });

        toast({
          description: `Solicitação de atualização de BDI em massa enviada com sucesso. Você será notificado quando a atualização for concluída.`,
          status: 'success',
        });

        if (onConfirm) onConfirm();
        handleClose();
      } catch (err) {
        toast({
          description:
            err.response?.data?.message ||
            'Houve um erro ao enviar a solicitação de atualização de BDI',
          status: 'error',
        });
      }
    },
    [handleClose, onConfirm, budgetId, bdiPercent],
  );

  const handleCheckComponentUsageInBudget = useCallback(async () => {
    const formErrors: FormErrors = {} as FormErrors;

    if (bdiPercent !== null && bdiPercent !== undefined && bdiPercent < 0) {
      Object.assign(formErrors, { bdiPercent: true, type: 'invalid' });
      toast({
        description: 'O BDI não pode ser negativo.',
        status: 'error',
      });
    }

    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      return;
    }

    try {
      setLoading(true);

      const allAppearances = await checkUsage(component);

      if (allAppearances.length === 0) {
        toast({
          description: 'Nenhum item encontrado para aplicar o BDI.',
          status: 'warning',
        });

        return;
      }

      if (allAppearances.length > 1) {
        openCustomAlert('budgetComponentListAlert', {
          type: 'warning',
          data: {
            components: allAppearances,
            bdi: bdiPercent,
          },
          onConfirm: handleSubmitCustomBDI,
        });
      } else {
        const [componentToUpdate] = allAppearances;
        handleSubmitCustomBDI([componentToUpdate.id]);
      }
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao enviar a solicitação de atualização de BDI',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [
    component,
    bdiPercent,
    checkUsage,
    openCustomAlert,
    handleSubmitCustomBDI,
  ]);

  return (
    <Modal {...restProps} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Editar BDI Diferenciado</ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <Flex
            w="100%"
            as="form"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={{
              '> *': {
                marginY: 1,
              },
            }}
          >
            <Heading
              fontSize="medium"
              fontWeight="600"
              width="100%"
              textAlign="left"
              mt={-2}
              mb={4}
            >
              Descrição
              <Text fontWeight="400" fontSize="sm" mt={2}>
                {description}
              </Text>
            </Heading>

            <Grid
              w="100%"
              templateColumns="repeat(auto-fit, minmax(300px, 1fr))"
              columnGap={2}
              rowGap={4}
            >
              <FormControl isInvalid={!!errors?.bdiPercent}>
                <FormLabel>BDI Diferenciado</FormLabel>
                <InputNumberFormat
                  type="tel"
                  customInput={ChakraInput}
                  displayType="input"
                  className="text-right"
                  placeholder="0 %"
                  fixedDecimalScale
                  thousandSeparator="."
                  decimalSeparator=","
                  suffix=" %"
                  decimalScale={2}
                  value={bdiPercent}
                  onFocus={(e) => e.target.select()}
                  onValueChange={(v) => {
                    if (v.floatValue) {
                      setErrors((prev) => ({
                        ...prev,
                        bdiPercent: false,
                      }));
                    }

                    setBDIPercent(v.floatValue);
                  }}
                />

                <FormErrorMessage>
                  {!!errors?.bdiPercent && 'Valor inválido'}
                </FormErrorMessage>
              </FormControl>
            </Grid>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            colorScheme="green"
            onClick={handleCheckComponentUsageInBudget}
          >
            Confirmar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalBudgetComponentBDI;
